import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Button, Stack, useMediaQuery, useTheme } from '@mui/material';
import ButtonTabs from '@ui/components/ButtonTabs';
import { PageLayout } from '@ui/components/PageLayout';
import { useHelpTopic } from '@ui/contexts';
import StyledSearchInput from '@ui/components/StyledSearchInput';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import GlobalSearchResult from './components/GlobalSearchResult';
import { useDispatch, useSelector } from 'react-redux';
import { globalSearchAsync } from '@application/features/globalSearch/globalSearchThunk';
import { clearResults } from '@application/features/globalSearch/globalSearchSlice';
import { APIStatus } from '@application/enums/api-status';
import { ModuleNames, ModuleRequestNames } from '@application/constants/module-names';
import { hasPermission } from '@application/helpers/permission-helper';
const GlobalSearchPage = () => {
    const theme = useTheme();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));
    const [autoFocus, setAutoFocus] = React.useState(false);
    React.useEffect(() => {
        if (localStorage.getItem('globalSearchAutoFocus') === 'true') {
            setAutoFocus(true);
            setTimeout(() => {
                localStorage.removeItem('globalSearchAutoFocus');
            }, 100);
        }
        // Clear results when page loads/initializes
        dispatch(clearResults());
    }, [dispatch]);
    const [searchValue, setSearchValue] = React.useState('');
    const [isDeepSearch, setIsDeepSearch] = React.useState(false);
    const { results = [], status } = useSelector((s) => s.globalSearch);
    const permissions = useSelector((s) => s.auth.permissions || []);
    const selectedCompanyId = useSelector((s) => s.company.selectedCompanyId);
    const loading = status === APIStatus.PENDING;
    // Map module names to their required permissions
    const modulePermissions = {
        events: 'Permissions.VdkEvents.Read',
        tasks: 'Permissions.VdkTasks.Read',
        people: 'Permissions.VdkPeople.Read',
        users: 'Permissions.Users.Read',
        externalrelation: 'Permissions.ExternalRelation.Read'
    };
    // Check if user has permission for a module
    const hasModulePermission = React.useCallback((moduleValue) => {
        if (moduleValue === 'all')
            return true;
        if (moduleValue === 'externalrelation')
            return true;
        const permission = modulePermissions[moduleValue];
        return permission ? hasPermission(permissions, permission) : false;
    }, [permissions]);
    // Filter tabs based on permissions
    const allTabOptions = React.useMemo(() => [
        { label: t('common.all'), value: 'all' },
        { label: t('common.events'), value: 'events' },
        { label: t('common.tasks'), value: 'tasks' },
        { label: t('common.people'), value: 'people' },
        { label: t('common.externalrelation'), value: 'externalrelation' }
    ], [t]);
    const tabOptions = React.useMemo(() => {
        return allTabOptions.filter((tab) => hasModulePermission(tab.value));
    }, [allTabOptions, hasModulePermission]);
    const secondTabOptions = [
        { label: t('globalSearch.top5'), value: 'top5' },
        { label: t('globalSearch.allResults'), value: 'all-results' }
    ];
    const [tab, setTab] = React.useState('all');
    useHelpTopic(tab);
    const [secondTab, setSecondTab] = React.useState(secondTabOptions[0].value);
    const typingDebounceRef = React.useRef(null);
    // Clear search results and reset search when company changes
    React.useEffect(() => {
        // Clear search value and results when company changes
        setSearchValue('');
        if (typingDebounceRef.current) {
            clearTimeout(typingDebounceRef.current);
        }
        dispatch(clearResults());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCompanyId]);
    // Set default tab based on available tabs and ensure current tab is valid
    React.useEffect(() => {
        if (tabOptions.length > 0 && !tabOptions.find((t) => t.value === tab)) {
            setTab(tabOptions[0].value);
        }
    }, [tabOptions, tab]);
    const handleResetCallback = () => {
        setSearchValue('');
        if (typingDebounceRef.current) {
            clearTimeout(typingDebounceRef.current);
        }
        dispatch(clearResults());
    };
    const normalizeEntityTypeForRequest = (t) => {
        switch (t) {
            case 'events':
                return ModuleRequestNames[ModuleNames.Events];
            case 'tasks':
                return ModuleRequestNames[ModuleNames.Tasks];
            case 'people':
                return ModuleRequestNames[ModuleNames.Peoples];
            case 'companies':
                return ModuleRequestNames[ModuleNames.Companies];
            case 'users':
                return ModuleRequestNames[ModuleNames.Users];
            case 'externalrelation':
                return ModuleRequestNames[ModuleNames.ExternalRelation];
            default:
                return t;
        }
    };
    const handleSearch = async () => {
        const term = searchValue.trim();
        if (!term || term.length < 2)
            return;
        const entityTypes = tab === 'all'
            ? [
                ModuleRequestNames[ModuleNames.Events],
                ModuleRequestNames[ModuleNames.Tasks],
                ModuleRequestNames[ModuleNames.Peoples],
                ModuleRequestNames[ModuleNames.ExternalRelation]
            ].filter((entityType) => {
                // Filter entity types based on permissions when "all" is selected
                const moduleValue = entityType === ModuleRequestNames[ModuleNames.Events]
                    ? 'events'
                    : entityType === ModuleRequestNames[ModuleNames.Tasks]
                        ? 'tasks'
                        : entityType === ModuleRequestNames[ModuleNames.Peoples]
                            ? 'people'
                            : entityType === ModuleRequestNames[ModuleNames.ExternalRelation]
                                ? 'externalrelation'
                                : '';
                return moduleValue ? hasModulePermission(moduleValue) : false;
            })
            : [normalizeEntityTypeForRequest(tab)];
        const showAll = secondTab === 'all-results';
        const body = {
            searchTerm: term,
            entityTypes,
            isDeepSearch,
            maxResultsPerEntity: 5,
            showAllResults: showAll
        };
        dispatch(globalSearchAsync(body));
    };
    // Debounced auto-search on typing
    React.useEffect(() => {
        if (typingDebounceRef.current) {
            clearTimeout(typingDebounceRef.current);
        }
        const term = searchValue.trim();
        if (!term) {
            return; // do not search on empty
        }
        if (term.length < 2) {
            // Clear results if less than 2 characters
            dispatch(clearResults());
            return; // do not search on less than 2 characters
        }
        typingDebounceRef.current = setTimeout(() => {
            handleSearch();
        }, 400);
        return () => {
            if (typingDebounceRef.current) {
                clearTimeout(typingDebounceRef.current);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchValue]);
    // Re-run search when filters/tabs change and we have a term
    React.useEffect(() => {
        const term = searchValue.trim();
        if (!term || term.length < 2)
            return;
        handleSearch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab, secondTab, isDeepSearch]);
    const groupByType = (type) => results.find((g) => g.entityType?.toLowerCase() === type.toLowerCase());
    const handleItemClick = (entityType, id) => {
        const normalizedType = entityType?.toLowerCase();
        if (normalizedType === 'events') {
            navigate(`/event-details/${id}`);
        }
        else if (normalizedType === 'tasks') {
            navigate('/tasks');
        }
        else if (normalizedType === 'peoples') {
            navigate(`/people/${id}`);
        }
        else if (normalizedType === 'externalrelation') {
            navigate('/relations');
        }
    };
    const mapItems = (group) => {
        if (!group)
            return [];
        return group.items.map((it) => ({
            id: it.id,
            title: it.title,
            entityType: it.entityType,
            meta: it.metaData.map((m) => ({ label: m.key, value: m.value }))
        }));
    };
    let activeTabContent = null;
    if (results.length > 0) {
        switch (tab) {
            case 'all':
                activeTabContent = (_jsx(Box, { display: 'flex', flexDirection: 'column', gap: 1, children: [ModuleNames.Events, ModuleNames.Tasks, ModuleNames.Peoples, ModuleNames.ExternalRelation]
                        .filter((et) => {
                        // Only show modules user has permission for
                        const moduleValue = et === ModuleNames.Events
                            ? 'events'
                            : et === ModuleNames.Tasks
                                ? 'tasks'
                                : et === ModuleNames.Peoples
                                    ? 'people'
                                    : et === ModuleNames.ExternalRelation
                                        ? 'externalrelation'
                                        : '';
                        return moduleValue ? hasModulePermission(moduleValue) : false;
                    })
                        .map((et) => {
                        const group = groupByType(et);
                        if (!group || group.items.length === 0)
                            return null;
                        const heading = et === ModuleNames.Events
                            ? t('common.events')
                            : et === ModuleNames.Tasks
                                ? t('common.tasks')
                                : et === ModuleNames.Peoples
                                    ? t('common.people')
                                    : t('common.externalrelation');
                        return (_jsx(GlobalSearchResult, { heading: heading, items: mapItems(group), searchTerm: searchValue, onItemClick: handleItemClick }, et));
                    }) }));
                break;
            case 'events':
                activeTabContent = (_jsx(GlobalSearchResult, { heading: t('common.events'), items: mapItems(groupByType(ModuleNames.Events)), searchTerm: searchValue, onItemClick: handleItemClick }));
                break;
            case 'tasks':
                activeTabContent = (_jsx(GlobalSearchResult, { heading: t('common.tasks'), items: mapItems(groupByType(ModuleNames.Tasks)), searchTerm: searchValue, onItemClick: handleItemClick }));
                break;
            case 'people':
                activeTabContent = (_jsx(GlobalSearchResult, { heading: t('common.people'), items: mapItems(groupByType(ModuleNames.Peoples)), searchTerm: searchValue, onItemClick: handleItemClick }));
                break;
            case 'externalrelation':
                activeTabContent = (_jsx(GlobalSearchResult, { heading: t('common.externalrelation'), items: mapItems(groupByType(ModuleNames.ExternalRelation)), searchTerm: searchValue, onItemClick: handleItemClick }));
                break;
            default:
                activeTabContent = null;
        }
    }
    else {
        activeTabContent = null;
    }
    const activeSecondTab = secondTabOptions.find((t) => t.value === secondTab);
    return (_jsxs(PageLayout, { title: t('common.globalSearch'), children: [_jsxs(Box, { display: 'flex', flexDirection: 'column', gap: isSmallScreen ? theme.spacing(0.5) : theme.spacing(1.5), width: '100%', children: [_jsxs(Box, { display: 'flex', gap: 1, width: '100%', children: [_jsx(StyledSearchInput, { autoFocus: autoFocus, bordered: true, fullWidth: true, value: searchValue, onChange: (e) => {
                                    const val = e.target.value;
                                    setSearchValue(val);
                                    if (val.trim() === '') {
                                        if (typingDebounceRef.current)
                                            clearTimeout(typingDebounceRef.current);
                                        dispatch(clearResults());
                                    }
                                }, onKeyDown: (e) => {
                                    if (e.key === 'Enter')
                                        handleSearch();
                                }, handleResetCallback: handleResetCallback }), _jsx(Button, { variant: 'outlined', color: 'primary', size: 'large', onClick: handleSearch, disabled: loading, children: t('common.search') })] }), _jsxs(Box, { display: 'flex', justifyContent: isSmallScreen ? undefined : 'space-between', flexDirection: isSmallScreen ? 'column-reverse' : undefined, gap: 0.5, children: [_jsx(Stack, { direction: 'row', spacing: 1, sx: {
                                    overflowX: 'auto',
                                    whiteSpace: 'nowrap',
                                    scrollbarWidth: 'none',
                                    maxWidth: '100%'
                                }, children: _jsx(ButtonTabs, { tabs: tabOptions, value: tab, onChange: setTab, variant: 'contained-white', size: 'medium' }) }), _jsx(Box, { display: 'flex', gap: 1, children: _jsx(ButtonTabs, { tabs: secondTabOptions, value: secondTab, onChange: setSecondTab, variant: 'contained-inside-white', size: 'medium', sx: {
                                        py: '6px'
                                    } }) })] })] }), _jsx(Box, { paddingTop: isSmallScreen ? theme.spacing(2) : theme.spacing(1.5), sx: {
                    overflowY: 'auto',
                    maxHeight: isSmallScreen ? 'calc(100dvh - 280px)' : 'calc(100dvh - 250px)',
                    flex: 1,
                    pb: 0.5,
                    minHeight: 0,
                    px: 0.5
                }, children: activeTabContent })] }));
};
export default GlobalSearchPage;
